import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import SignaturePad from "signature_pad";

interface CountersignerZoneProps {
  name: string;
  signatureKey: string | null;
  cdnBaseUrl: string;
  onChangeName: (next: string) => void;
  onChangeSignatureKey: (next: string | null) => void;
}

interface UploadResponse {
  storage_key: string;
  cdn_url: string;
}

/**
 * Countersigner signature input. Two ways in:
 *
 * - Upload — hidden file picker, accepts PNG/JPEG/WebP, posts to the
 *   shared `/api/agreements/images/upload` endpoint and stores the
 *   returned `storage_key`.
 * - Draw — collapses an inline signature_pad canvas open. Save uploads
 *   the rendered PNG; Clear wipes the pad; Cancel collapses without
 *   changing anything.
 *
 * The preview row keeps the compact "name · signature" treatment so
 * this component still slots cleanly into a settings panel. Mirrors
 * reflex-admin's CountersignerInput behaviorally; styling is portal-
 * native.
 */
export function CountersignerZone({
  name,
  signatureKey,
  cdnBaseUrl,
  onChangeName,
  onChangeSignatureKey,
}: CountersignerZoneProps) {
  const [drawing, setDrawing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePad | null>(null);

  const signatureUrl = signatureKey
    ? `${cdnBaseUrl.replace(/\/$/, "")}/${signatureKey}`
    : null;

  // ---- upload helper, shared by both modes -----------------------------
  const upload = async (blob: Blob, filename: string) => {
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      fd.append("type", "signature");
      const res = await fetch("/api/agreements/images/upload", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null;
        throw new Error(body?.error ?? `Upload failed (${res.status})`);
      }
      const data = (await res.json()) as UploadResponse;
      onChangeSignatureKey(data.storage_key);
      setDrawing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // ---- upload mode -----------------------------------------------------
  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await upload(file, file.name);
  };

  // ---- draw mode -------------------------------------------------------
  // Initialize signature_pad on canvas mount; tear down on exit.
  useEffect(() => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d")?.scale(ratio, ratio);

    const pad = new SignaturePad(canvas, {
      penColor: "#0B2545", // ink
      minWidth: 0.5,
      maxWidth: 2.5,
      backgroundColor: "rgba(255,255,255,0)",
    });
    padRef.current = pad;

    return () => {
      pad.off();
      padRef.current = null;
    };
  }, [drawing]);

  const handleSaveDrawn = async () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      setError("Draw a signature first.");
      return;
    }
    const dataUrl = pad.toDataURL("image/png");
    const blob = await (await fetch(dataUrl)).blob();
    await upload(blob, "signature.png");
  };

  const handleClearDrawn = () => {
    padRef.current?.clear();
    setError(null);
  };

  const handleRemove = () => {
    onChangeSignatureKey(null);
    setError(null);
  };

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={name || ""}
        onChange={(e) => onChangeName(e.target.value)}
        placeholder="Name · Title"
        className="hair w-full rounded-md border bg-canvas/30 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
      />

      <div className="hair flex items-center gap-3 rounded-md border bg-canvas/20 p-2">
        {signatureUrl ? (
          <img
            src={signatureUrl}
            alt="Countersigner signature"
            className="h-10 max-w-[160px] object-contain px-2"
          />
        ) : (
          <div className="font-display px-3 text-[20px] italic text-ink-2">
            {name || "—"}
          </div>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="hair rounded border bg-paper-hi px-2 py-1 text-[10.5px] text-ink-4 hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {uploading && !drawing ? "Uploading…" : "Upload"}
          </button>
          <button
            type="button"
            onClick={() => setDrawing((d) => !d)}
            disabled={uploading}
            aria-pressed={drawing}
            className={`hair rounded border px-2 py-1 text-[10.5px] hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-60 ${
              drawing
                ? "border-tide/40 bg-tide-ss/60 text-tide"
                : "bg-paper-hi text-ink-4"
            }`}
          >
            {drawing ? "Cancel" : "Draw"}
          </button>
          {signatureKey && !drawing && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={uploading}
              aria-label="Remove signature"
              className="hair flex h-[22px] w-[22px] items-center justify-center rounded border bg-paper-hi text-ink-4 hover:bg-rust-s/40 hover:text-rust disabled:cursor-not-allowed disabled:opacity-60"
            >
              <X size={11} strokeWidth={2.2} />
            </button>
          )}
        </div>
      </div>

      {drawing && (
        <div>
          <div className="hair rounded-md border border-ink/10 bg-paper-hi p-2">
            <canvas
              ref={canvasRef}
              role="img"
              aria-label="Drawn signature"
              style={{
                width: "100%",
                height: 120,
                display: "block",
                touchAction: "none",
              }}
            />
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleSaveDrawn}
              disabled={uploading}
              className="rounded-md bg-tide px-3 py-1 text-[11.5px] font-medium text-paper-hi hover:bg-tide-d disabled:cursor-not-allowed disabled:bg-ink-6"
            >
              {uploading ? "Saving…" : "Save signature"}
            </button>
            <button
              type="button"
              onClick={handleClearDrawn}
              disabled={uploading}
              className="hair rounded-md border bg-paper-hi px-3 py-1 text-[11.5px] text-ink-3 hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Clear
            </button>
            <span className="ml-auto text-[10.5px] text-ink-5">
              Use mouse or touch to draw
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="text-[11px] text-rust" role="alert">
          {error}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFile}
      />
    </div>
  );
}
